import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'gatsby';
import styles from '@utils/styles';
import breakpoints from '@rsc/ui-components/breakpoints';
import { MavenoidChatFooter } from '@rsc/ui-components/openChatWidget';
import ArrowIcon from '@components/svgs/arrow-icon.svg';
import Newsletter from '@components/footer/newsletter';
import SocialIcons from '@components/footer/SocialIcons';
import KurgoMountains from '../../../static/images/kurgo-site-mountains_1920wide.jpg';

const StyledFooter = styled.footer`
  background-color: #e8e8e8;
  padding-top: 4rem;
  padding-bottom: 1rem;
  background-image: url(${KurgoMountains});
  background-repeat: no-repeat;

  @media (min-width: ${breakpoints.md}) {
    background-size: cover;
  }
`;

const Container = styled.div`
  margin: 0 auto;
  @media (min-width: ${breakpoints.md}) {
    max-width: 1280px;
  }
`;

const InnerContainer = styled.div`
  max-width: none;
  width: auto;
`;

const FooterSection = styled.div`
  width: 100%;
  padding: 0 0.75rem;
  color: ${styles.colors.gray[700]};
  display: inline-block;
  font-size: ${styles.fontSize.base};
  vertical-align: top;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.lg}) {
    &:nth-child(4) {
      width: 100%;
      margin-top: 40px;
    }
  }

  @media (min-width: ${breakpoints.md}) {
    width: 33%;
  }

  @media (min-width: ${breakpoints.lg}) {
    width: 25%;
  }
`;

const FooterHeading = styled.h2`
  font-size: ${styles.fontSize.sm};
  font-weight: ${styles.fontWeight.medium};
  color: ${styles.colors.gray[800]};
  position: relative;

  @media (max-width: ${breakpoints.md}) {
    cursor: pointer;
  }

  i {
    display: inline-block;
    position: absolute;
    width: 24px;
    height: 24px;
    right: 0;
    top: 50%;
    margin-top: -12px;

    @media (min-width: ${breakpoints.md}) {
      display: none;
    }
  }
`;

const CopyrightContainer = styled.div`
  padding: 15px 0.75rem;
  text-align: center;

  @media (min-width: ${breakpoints.lg}) {
    text-align: left;
  }

  div {
    color: ${styles.colors.gray[600]};
    font-size: ${styles.fontSize['1xs']};
    margin: 0;

    > button {
      background: transparent;
      border: none;
      font: inherit;
      color: inherit;
      line-height: inherit;
      padding: 0;
      cursor: pointer;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const FooterToogleContent = styled.div`
  @media (max-width: ${breakpoints.md}) {
    overflow: hidden;
    max-height: 0;
    transition: max-height 1s cubic-bezier(0, 1, 0, 1);

    &.show {
      max-height: 1000px;
      transition: max-height 1s ease-in-out;
    }
  }

  @media (min-width: ${breakpoints.md}) {
    display: block;
    max-height: 100%;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0 0 ${styles.spacing['spacing-lg']} 0;
  }

  li {
    line-height: 1.5;
    margin-top: ${styles.spacing['spacing-1xs']};
  }

  li.footer-sub-heading {
    margin-top: 20px;

    span {
      font-weight: ${styles.fontWeight.medium};
      color: ${styles.colors.gray[700]};
      font-size: ${styles.fontSize.sm};
    }

    p {
      font-weight: ${styles.fontWeight.light};
      font-size: ${styles.fontSize.sm};
      margin: 0;
      padding: 0;
      line-height: 1.5;
    }
  }

  a {
    font-size: ${styles.fontSize.sm};
    line-height: 16px;
    color: ${styles.colors.gray[700]};
    font-weight: ${styles.fontWeight.light};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Footer = () => {
  const [companyVisible, setCompanyVisible] = useState(false);
  const [supportVisible, setSupportVisible] = useState(false);
  const [contactVisible, setContactVisible] = useState(false);

  return (
    <StyledFooter>
      <Container>
        <InnerContainer>
          <FooterSection>
            <FooterHeading onClick={() => setCompanyVisible(!companyVisible)}>
              Company
              <i>
                <ArrowIcon transform={companyVisible ? 'rotate(180)' : ''} />
              </i>
            </FooterHeading>
            <FooterToogleContent
              className={companyVisible && 'show'}
              aria-describedby="Company Menu"
            >
              <ul>
                <li>
                  <Link to="/about-us">Our Story</Link>
                </li>
                <li>
                  <Link to="/blog">Blog</Link>
                </li>
                <li>
                  <Link to="/giving-back">Adventure Together Fund</Link>
                </li>
                <li>
                  <Link to="/careers">Careers</Link>
                </li>
                <li>
                  <Link to="/in-the-media">Media</Link>
                </li>
                <li>
                  <Link to="/kurgo-affiliate-program">Affiliate Program</Link>
                </li>
                <li>
                  <Link to="/store-locator">Store Locator</Link>
                </li>
              </ul>
            </FooterToogleContent>
          </FooterSection>
          <FooterSection>
            <FooterHeading onClick={() => setSupportVisible(!supportVisible)}>
              Help &amp; Support
              <i>
                <ArrowIcon transform={supportVisible ? 'rotate(180)' : ''} />
              </i>
            </FooterHeading>
            <FooterToogleContent
              className={supportVisible && 'show'}
              aria-describedby="Help &amp; Support Menu"
            >
              <ul>
                <li>
                  <Link to="/resource-center">Resource Center</Link>
                </li>
                <li>
                  <Link to="/lifetime-warranty">Lifetime Warranty</Link>
                </li>
                <li>
                  <Link to="/lifetime-warranty-form">Warranty Claim Form</Link>
                </li>
                <li>
                  <Link to="/safe-again-program">Safe Again Program</Link>
                </li>
                <li>
                  <Link to="/shipping-returns">Shipping &amp; Returns</Link>
                </li>
                <li>
                  <Link to="/product-instructions">Product Instructions</Link>
                </li>
                <li>
                  <Link to="/privacy">Privacy Policy</Link>
                </li>
                <li>
                  <Link to="/rsg">Molle</Link>
                </li>
              </ul>
            </FooterToogleContent>
          </FooterSection>
          <FooterSection>
            <FooterHeading onClick={() => setContactVisible(!contactVisible)}>
              Contact
              <i>
                <ArrowIcon transform={contactVisible ? 'rotate(180)' : ''} />
              </i>
            </FooterHeading>
            <FooterToogleContent
              className={contactVisible && 'show'}
              aria-describedby="Contact Menu"
            >
              <ul>
                <li>
                  <Link to="/contact-us">Contact Us</Link>
                </li>
                <li className="footer-sub-heading">
                  <span>Live Chat:</span>
                  <p>
                    <MavenoidChatFooter />
                  </p>
                </li>
                <li className="footer-sub-heading">
                  <span>Phone:</span>
                  <p>
                    <a href="tel:+18778473868">+1 877-847-3868</a>
                  </p>
                </li>
                <li className="footer-sub-heading">
                  <span>Email:</span>
                  <p>
                    <a href="mailto:info@kurgo.com">info@kurgo.com</a>
                  </p>
                </li>
                <li className="footer-sub-heading">
                  <span>Location:</span>
                  <p>
                    Kurgo Products
                    <br />
                    10427 PetSafe Way
                    <br />
                    Knoxville, TN 37932
                  </p>
                </li>
              </ul>
            </FooterToogleContent>
          </FooterSection>
          <FooterSection>
            <FooterHeading>Newsletter Sign Up</FooterHeading>
            <Newsletter />
            <SocialIcons />
          </FooterSection>
          <div className="be-ix-link-block" />
          {/* BrightEdge */}
        </InnerContainer>
        <CopyrightContainer>
          <div>
            &copy; {new Date().getFullYear()}
            {` `}
            <a href="https://www.petsafebrands.com/" rel="license">
              Radio Systems Corporation
            </a>
            {` `}
            All Rights Reserved.{' '}
            <button
              type="button"
              aria-label="Open cookie settings modal"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window?.OneTrust?.ToggleInfoDisplay();
                }
              }}
            >
              Cookie Settings
            </button>
          </div>
        </CopyrightContainer>
      </Container>
    </StyledFooter>
  );
};

export default Footer;
